/* eslint-disable no-inner-declarations */
import {
  DealVersion as StoreDeal,
  Order as StoreOrder,
  Maturity as StoreMaturity,
  Series as StoreSeries,
  SyndicateMember as StoreSyndicateMember,
  SecurityType
} from '@ipreo/muni-shared';
import { cloneDeep, omit, isEqual } from 'lodash';

const combineFn = (fn) => (p, m) => {
  p[m.id] = fn(m);
  return p;
};

const combineImmutable = combineFn((m) => ({ ...m }));

export namespace GameDayModel {
  export interface RawData {
    deal: Deal;
    orders: Order[];
  }

  export interface SyndicateMember extends StoreSyndicateMember {
    name: string;
  }

  export interface Deal extends StoreDeal {
    maturities: Maturity[];
    series: Series[];
    syndicate: SyndicateMember[];
  }

  export enum AccountPriority {
    stock = 0,
    undisclosed = 1,
    individuals = 2,
    regular = 3,
    brockerDealer = 4
  }

  export enum OrderPriority {
    stock = 0,
    institutional = 1,
    retail = 2
  }

  export enum RetailType {
    full = 0,
    lowPriority = 1,
    lowPriorityInstitutional = 2,
    other = 3
  }

  export interface AccountInfo {
    name: string;
    priority: AccountPriority;
  }

  export interface Order extends StoreOrder {
    series: Series;
    maturity: Maturity;
    accountInfo: AccountInfo;
    priority: OrderPriority;
    retailType: RetailType;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  export interface Series extends StoreSeries {}

  export interface Maturity extends StoreMaturity {
    series: Series;
  }

  export interface OrderPriorityConditions {
    codes: string[];
    retailInd: boolean;
  }

  export const INSTITUTIONAL = 'Institutional';
  export const RETAIL = 'Retail';

  export const BROCKER_DEALER_ACCOUNT_TYPE_CODE = 'BD';
  export const LOW_PRIORITY_RETAIL_TYPE_CODE = 'R';

  export const INDIVIDUALS = 'Individuals';

  export const UNDISCLOSED = 'Undisclosed';

  export const STOCK = 'Stock';

  export const GENERATED_ACCOUNTS = [UNDISCLOSED, INDIVIDUALS, STOCK];

  // Order type conditions
  export const STOCK_TYPE: OrderPriorityConditions = {
    retailInd: false,
    codes: ['MEM', 'PS']
  };
  export const INSTITUTIONAL_TYPE: OrderPriorityConditions = {
    retailInd: false,
    codes: ['ND', 'GN', 'PSND', 'PSGN']
  };
  export const RETAIL_LOW_PRIORITY_TYPE: OrderPriorityConditions = {
    retailInd: false,
    codes: ['MEM', 'PS']
  };

  // Account type conditions
  export const ACCOUNT_RETAIL_TYPE: OrderPriorityConditions = {
    retailInd: false,
    codes: [LOW_PRIORITY_RETAIL_TYPE_CODE]
  };
  export const ACCOUNT_INSTITUTIONAL_TYPE: OrderPriorityConditions = {
    retailInd: false,
    codes: [
      'I',
      BROCKER_DEALER_ACCOUNT_TYPE_CODE,
      LOW_PRIORITY_RETAIL_TYPE_CODE
    ]
  };
  export const ACCOUNT_STOCK_TYPE: OrderPriorityConditions = {
    retailInd: false,
    codes: ['IN', 'S', 'STOCK']
  };

  export function isRawDataEqual(data1: RawData, data2: RawData): boolean {
    return (
      isEqual(data1.deal, data1.deal) &&
      areOrdersEqual(data1.orders, data2.orders)
    );
  }

  function areOrdersEqual(orders1: Order[], orders2: Order[]): boolean {
    if (orders1.length !== orders2.length) {
      return false;
    }
    const length = orders1.length;
    for (let i = 0; i < length; i++) {
      if (orders1[i].lastModifiedDate !== orders2[i].lastModifiedDate) {
        return false;
      }
    }

    return true;
  }

  export function fromStoreModels(
    storeDeal: StoreDeal,
    storeOrders: StoreOrder[]
  ): RawData {
    const seriesMap: { [id: string]: Series } = storeDeal.series.reduce(
      combineImmutable,
      {}
    );

    const deal = {
      ...cloneDeep(
        omit(storeDeal, 'maturities', 'syndicate', 'gamedaySettings')
      ),
      gamedaySettings: {
        ...storeDeal.gamedaySettings,
        showAccountNames: true // FIXME: REMOVE! JUST BECAUSE API IS BROKEN!!!!
      },
      maturities: storeDeal.maturities.map((storeMaturity) =>
        convertMaturity(storeMaturity, seriesMap)
      ),
      syndicate:
        storeDeal.syndicate &&
        storeDeal.syndicate.map((member) => ({
          ...member,
             name:
               (member as { name?: string }).name ||
               member.shortName ||
               member.departmentName
        }))
    };

    const maturitiesMap = deal.maturities.reduce(combineImmutable, {});

    const orders = storeOrders
      .map((storeOrder) =>
        convertOrder(storeOrder, deal, seriesMap, maturitiesMap)
      )
      .filter((order) => {
        return order.priority !== undefined && order.maturity !== undefined;
      });

    return { deal, orders };
  }

  function convertMaturity(
    storeMaturity: StoreMaturity,
    seriesMap: { [id: string]: Series }
  ): Maturity {
    const maturity = {
      ...cloneDeep(storeMaturity),
      series: seriesMap[storeMaturity.seriesId]
    };

    if (
      maturity.coupon === null &&
      maturity.securityType === SecurityType.zero
    ) {
      maturity.coupon = 0;
    }

    return maturity;
  }

  const ACCOUNT_INDIVIDUALS_INFO: AccountInfo = {
    name: INDIVIDUALS,
    priority: AccountPriority.individuals
  };
  const ACCOUNT_UNDISCLOSED_INFO: AccountInfo = {
    name: UNDISCLOSED,
    priority: AccountPriority.undisclosed
  };
  const ACCOUNT_STOCK_INFO: AccountInfo = {
    name: '',
    priority: AccountPriority.stock
  };

  function convertOrder(
    storeOrder: StoreOrder,
    deal: Deal,
    seriesMap: { [id: string]: Series },
    maturitiesMap: { [id: string]: Maturity }
  ): Order {
    const isSoleManagedDeal = deal.syndicate.length === 1;
    const syndicateDepartment = getSyndicateDepartment(storeOrder, deal);

    const priority = getOrderPriority(storeOrder);
    const retailType = getOrderRetailType(storeOrder, priority);

    const order = {
      ...cloneDeep(storeOrder),
      syndicateDepartment,
      series: seriesMap[storeOrder.seriesId],
      maturity: maturitiesMap[storeOrder.maturityId],
      accountInfo: getOrderAccountInfo(storeOrder, priority, retailType, deal),
      firmId: orderIsBrokerDealer(storeOrder)
        ? storeOrder.brokerDealerFirmId
        : storeOrder.firmId,
      priority,
      retailType
    };

    if (isSoleManagedDeal && !order.accountType) {
      order.accountInfo = order.retailInd
        ? ACCOUNT_INDIVIDUALS_INFO
        : ACCOUNT_STOCK_INFO;
      order.priority =
        order.priority === OrderPriority.institutional
          ? OrderPriority.stock
          : order.priority;
    }

    if (isSoleManagedDeal && order.retailType === RetailType.lowPriority) {
      order.priority = OrderPriority.institutional;
      order.retailType = RetailType.lowPriorityInstitutional;
    }

    return order;
  }

  function getSyndicateDepartment(
    storeOrder: StoreOrder,
    deal: Deal
  ): SyndicateMember {
    const orderManager = (storeOrder as unknown as {
      manager?:
        | string
        | {
            name?: string;
            shortName?: string;
            departmentName?: string;
          };
    }).manager;
    const orderManagerName =
      typeof orderManager === 'string'
        ? orderManager
        : orderManager?.name ||
          orderManager?.shortName ||
          orderManager?.departmentName;
    const syndicateDepartment = cloneDeep(
      storeOrder.syndicateDepartment || {}
    ) as Partial<SyndicateMember> & {
      shortName?: string;
      departmentName?: string;
      name?: string;
    };
    const fallbackManagerName =
      syndicateDepartment.name ||
      syndicateDepartment.shortName ||
      syndicateDepartment.departmentName ||
      orderManagerName ||
      deal.seniorManager?.shortName ||
      deal.seniorManager?.name;
    const syndicateDepartmentId = syndicateDepartment.id;
    const dealSyndicateMember = deal.syndicate.find(
      (member) =>
        syndicateDepartmentId !== undefined &&
        syndicateDepartmentId !== null &&
        String(member.id) === String(syndicateDepartmentId)
    );

    if (!dealSyndicateMember) {
      if (fallbackManagerName) {
        return {
          ...syndicateDepartment,
          name: fallbackManagerName
        } as SyndicateMember;
      }

      // Some upgraded payloads can miss order-level manager linkage.
      // For sole-managed deals, use the only syndicate member as a safe fallback.
      if (
        deal.syndicate.length === 1 &&
        !fallbackManagerName
      ) {
        const soleSyndicateMember = deal.syndicate[0];
        return {
          ...soleSyndicateMember,
          ...syndicateDepartment,
          name:
            syndicateDepartment.name ||
            syndicateDepartment.shortName ||
            syndicateDepartment.departmentName ||
            soleSyndicateMember.name
        };
      }

      return syndicateDepartment as SyndicateMember;
    }

    return {
      ...dealSyndicateMember,
      ...syndicateDepartment,
      name:
        syndicateDepartment.name ||
        syndicateDepartment.shortName ||
        syndicateDepartment.departmentName ||
        dealSyndicateMember.name
    };
  }

  function getOrderRetailType(
    order: StoreOrder,
    priority: OrderPriority
  ): RetailType {
    switch (priority) {
      case OrderPriority.retail:
        return orderIsLowPriority(order)
          ? RetailType.lowPriority
          : RetailType.full;
      case OrderPriority.stock:
        return RetailType.other;
      case OrderPriority.institutional:
        return orderIsLowPriorityInstitutional(order, priority)
          ? RetailType.lowPriorityInstitutional
          : RetailType.other;
      default:
        return RetailType.other;
    }
  }

  function orderIsLowPriorityInstitutional(
    order: StoreOrder,
    priority: OrderPriority
  ): boolean {
    return (
      priority === OrderPriority.institutional &&
      order.accountType &&
      order.accountType.code === LOW_PRIORITY_RETAIL_TYPE_CODE
    );
  }

  function getOrderPriority(order: StoreOrder): OrderPriority {
    if (orderIsRetail(order)) {
      return OrderPriority.retail;
    }
    if (orderIsStock(order)) {
      return OrderPriority.stock;
    }
    if (orderIsInstitutional(order)) {
      return OrderPriority.institutional;
    }

    return undefined;
  }

  function orderIsRetail(order: StoreOrder): boolean {
    return order.retailInd || orderIsLowPriority(order);
  }

  function orderIsStock(order: StoreOrder): boolean {
    return order.accountType
      ? checkAccountType(order, ACCOUNT_STOCK_TYPE)
      : checkOrderType(order, STOCK_TYPE);
  }

  function orderIsInstitutional(order: StoreOrder): boolean {
    const { accountType } = order;

    if (accountType && accountType.code === LOW_PRIORITY_RETAIL_TYPE_CODE) {
      return checkOrderTypeAndAccount(
        order,
        ACCOUNT_INSTITUTIONAL_TYPE,
        INSTITUTIONAL_TYPE
      );
    }

    return accountType
      ? checkAccountType(order, ACCOUNT_INSTITUTIONAL_TYPE)
      : checkOrderType(order, INSTITUTIONAL_TYPE);
  }

  function orderIsLowPriority(order: StoreOrder): boolean {
    return checkOrderTypeAndAccount(
      order,
      ACCOUNT_RETAIL_TYPE,
      RETAIL_LOW_PRIORITY_TYPE
    );
  }

  function getOrderAccountInfo(
    order: StoreOrder,
    priority: OrderPriority,
    retailType: RetailType,
    deal: StoreDeal
  ): AccountInfo {
    let result: AccountInfo;

    if (!order.accountName || !(order.firmId || order.brokerDealerFirmId)) {
      switch (priority) {
        case OrderPriority.retail:
          result = {
            name: INDIVIDUALS,
            priority: AccountPriority.individuals
          };
          break;
        case OrderPriority.institutional:
          result = getInstitutionalAccountInfo(retailType);
          break;
        case OrderPriority.stock:
          result = {
            name: order.accountName,
            priority: AccountPriority.stock
          };
          break;
        default:
          break;
      }
    } else if (orderIsBrokerDealer(order)) {
      result = {
        name: order.accountName,
        priority: AccountPriority.brockerDealer
      };
    } else {
      result = {
        name: order.accountName,
        priority: AccountPriority.regular
      };
    }

    if (
      result &&
      !!deal.gamedaySettings &&
      !deal.gamedaySettings.showAccountNames
    ) {
      result.name = '';
    }

    return result;
  }

  function getInstitutionalAccountInfo(retailType: RetailType): AccountInfo {
    if (retailType === RetailType.lowPriorityInstitutional) {
      return ACCOUNT_INDIVIDUALS_INFO;
    }

    return ACCOUNT_UNDISCLOSED_INFO;
  }

  function orderIsBrokerDealer(order: StoreOrder): boolean {
    return (
      order.accountType &&
      order.accountType.code === BROCKER_DEALER_ACCOUNT_TYPE_CODE
    );
  }

  function checkOrderTypeAndAccount(
    order: StoreOrder,
    accountTypeConditions: OrderPriorityConditions,
    orderTypeConditions: OrderPriorityConditions
  ): boolean {
    return (
      !!order.accountType &&
      !!order.orderType &&
      checkAccountType(order, accountTypeConditions) &&
      checkOrderType(order, orderTypeConditions)
    );
  }

  function checkOrderType(
    order: StoreOrder,
    conditions: OrderPriorityConditions
  ): boolean {
    return (
      order.retailInd === conditions.retailInd &&
      !!order.orderType &&
      conditions.codes.indexOf(order.orderType.code) > -1
    );
  }

  function checkAccountType(
    order: StoreOrder,
    conditions: OrderPriorityConditions
  ): boolean {
    return (
      order.retailInd === conditions.retailInd &&
      !!order.accountType &&
      conditions.codes.indexOf(order.accountType.code) > -1
    );
  }
}
